import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { Card, CardHeader, CardContent } from '../Common/Card';
import { formatDistanceToNow } from 'date-fns';

const WorkflowExecutions = ({ onShowApiLogs }) => {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    workflow_id: '',
    page: 1,
    per_page: 20
  });
  const [selectedExecution, setSelectedExecution] = useState(null);

  useEffect(() => {
    fetchExecutions();
  }, [filters]);


  const fetchExecutions = async () => {
    try {
      setLoading(true);
      const token = Cookies.get('sankalvax_token');

      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.workflow_id) queryParams.append('workflow_id', filters.workflow_id);
      queryParams.append('page', filters.page);
      queryParams.append('per_page', filters.per_page);

      const response = await fetch(`/api/chat/executions?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch executions: ${response.statusText}`);
      }

      const data = await response.json();
      setExecutions(data.executions || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching executions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      completed: 'bg-green-100 text-green-800 border-green-200',
      failed: 'bg-red-100 text-red-800 border-red-200',
      running: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      pending: 'bg-blue-100 text-blue-800 border-blue-200'
    };

    const statusIcons = {
      completed: '✅',
      failed: '❌',
      running: '🔄',
      pending: '⏸️'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusStyles[status] || statusStyles.pending}`}>
        <span className="mr-1">{statusIcons[status] || '❓'}</span>
        {status}
      </span>
    );
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
    return `${seconds.toFixed(1)}s`;
  };

  const handleExecutionClick = (execution) => {
    setSelectedExecution(execution);
    // We'll implement the detail view next
  };

  if (loading && executions.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading workflow executions...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex justify-end">
        <button
          onClick={fetchExecutions}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          disabled={loading}
        >
          {loading ? '🔄 Refreshing...' : '🔄 Refresh'}
        </button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="running">Running</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Workflow ID</label>
              <input
                type="text"
                value={filters.workflow_id}
                onChange={(e) => setFilters(prev => ({ ...prev, workflow_id: e.target.value, page: 1 }))}
                placeholder="Enter workflow ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Per Page</label>
              <select
                value={filters.per_page}
                onChange={(e) => setFilters(prev => ({ ...prev, per_page: parseInt(e.target.value), page: 1 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Card>
          <CardContent>
            <div className="text-red-600 bg-red-50 p-4 rounded-lg border border-red-200">
              <div className="font-medium">Error loading executions:</div>
              <div className="mt-1 text-sm">{error}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Executions Table */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">
            V2 Workflow Executions ({executions.length} total)
          </h2>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Execution ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Workflow
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Step
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Started
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {executions.map((execution) => (
                  <tr key={execution.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      <div className="truncate w-32" title={execution.trace_id}>
                        {execution.trace_id.substring(0, 8)}...
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="max-w-32 truncate" title={execution.workflow_name}>
                        {execution.workflow_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(execution.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                        {execution.current_step}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div title={new Date(execution.started_at).toLocaleString()}>
                        {formatDistanceToNow(new Date(execution.started_at), { addSuffix: true })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDuration(execution.duration_seconds)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleExecutionClick(execution)}
                        className="text-blue-600 hover:text-blue-900 font-medium"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => onShowApiLogs && onShowApiLogs(execution.trace_id)}
                        className="text-green-600 hover:text-green-900 font-medium"
                      >
                        API Logs
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {executions.length === 0 && !loading && (
            <div className="text-center py-12">
              <div className="text-gray-500 text-lg">No workflow executions found</div>
              <div className="text-gray-400 text-sm mt-2">
                Try adjusting your filters or run a workflow to see executions here
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Selected Execution Detail (placeholder) */}
      {selectedExecution && (
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Execution Details: {selectedExecution.trace_id}</h3>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Workflow Name</label>
                <div className="mt-1 text-sm text-gray-900">{selectedExecution.workflow_name}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <div className="mt-1">{getStatusBadge(selectedExecution.status)}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Started At</label>
                <div className="mt-1 text-sm text-gray-900 font-mono">
                  {new Date(selectedExecution.started_at).toLocaleString()}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Duration</label>
                <div className="mt-1 text-sm text-gray-900">
                  {formatDuration(selectedExecution.duration_seconds)}
                </div>
              </div>
              {selectedExecution.error_message && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Error Message</label>
                  <div className="mt-1 text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-200 font-mono">
                    {selectedExecution.error_message}
                  </div>
                </div>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={() => setSelectedExecution(null)}
                className="text-gray-600 hover:text-gray-900"
              >
                Close Details
              </button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WorkflowExecutions;
